import type { FeatureContext } from './featureContext';
import { classifyIgnoreChat } from '../ignoreCommand';
import { canIgnoreName } from '../ignoreList';
import { isIgnorableTarget } from './ignoreTarget';

/**
 * The `/ignore` squelch pick (ADR-0185): the slash command arms the shared blue
 * target cursor for the `'ignore'` purpose - the twin of `/duel`, but a pick adds
 * the clicked player to the per-viewer squelch list instead of opening a wager
 * prompt (no server round-trip). The hover/pick gate swaps to the not-range-limited
 * `isIgnorableTarget` while armed. The list itself lives on the shared state (the
 * chat installer's suppression and the Settings panel edit the same store).
 */
export function installIgnore(ctx: FeatureContext): void {
  // `/ignore` is a client-side command, suppressed so the raw `/ignore` never
  // broadcasts as a chat line - exactly like `/dress` and `/duel`.
  ctx.chatCommands.intercept((text) => {
    const intent = classifyIgnoreChat(text);
    if (intent.arm) ctx.state.armTargeting('ignore');
    return intent.suppress;
  });

  // The armed cursor's eligibility filter (ADR-0185): **not** range-limited and not
  // NPC-gated, so you can squelch anyone named in view, near or far.
  ctx.state.setTargetFilter('ignore', (id) =>
    isIgnorableTarget(ctx.world(), ctx.playerId(), ctx.viewerName(), id),
  );

  // An `/ignore` pick adds the picked player to the per-viewer squelch list - no
  // prompt. The name/self guards are `canIgnoreName` (a nameless sprite or your own
  // is silently a no-op), so a fizzled pick is fine.
  ctx.state.onTargetPick('ignore', (target) => {
    const name = ctx.world()?.get(target)?.renderable?.name?.trim();
    if (name && canIgnoreName(name, ctx.viewerName())) ctx.state.ignoreList.add(name);
  });
}
